import type { ReactElement } from 'react';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { TipMenuItemSummary } from '@fetmode/types';
import { ApiError } from '@fetmode/api-client';
import { useAuth, useAuthModal } from '@fetmode/app-core';
import { t } from '@fetmode/i18n';
import { Button } from '@fetmode/ui';

import { useCoinShop } from '../wallet/index.js';
import { PlayerMenuDock } from './PlayerMenuDock.js';

export interface TipMenuBarProps {
  showId: string | null;
  modelUsername: string;
  canTip: boolean;
  coinBalance: number;
  isTipping: boolean;
  onBalanceChange: (total: number) => void;
  /** Player element — tip menu docks inside it (required for correct placement). */
  playerEl: HTMLElement | null;
  menuOpen: boolean;
  onMenuOpenChange: (open: boolean) => void;
  /** When another action expands, collapse this slide-out. */
  expanded: boolean;
  onExpandedChange: (open: boolean) => void;
}

export function TipMenuBar({
  showId,
  modelUsername,
  canTip,
  coinBalance,
  isTipping,
  onBalanceChange,
  playerEl,
  menuOpen,
  onMenuOpenChange,
  expanded,
  onExpandedChange,
}: TipMenuBarProps): ReactElement | null {
  const { api } = useAuth();
  const { requireAuth } = useAuthModal();
  const { requireCoins } = useCoinShop();
  const [items, setItems] = useState<TipMenuItemSummary[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [statusNote, setStatusNote] = useState<string | null>(null);
  const [pendingInteractionId, setPendingInteractionId] = useState<string | null>(null);
  const groupRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let cancelled = false;

    void api
      .listModelTipMenu(modelUsername)
      .then((result) => {
        if (!cancelled) {
          setItems(result.items);
          setLoaded(true);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          if (!(err instanceof ApiError && err.status === 404)) {
            setItems([]);
          }
          setLoaded(true);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [api, modelUsername]);

  useEffect(() => {
    if (!expanded) {
      return;
    }
    const onPointerDown = (event: PointerEvent): void => {
      const target = event.target;
      if (!(target instanceof Node) || !groupRef.current?.contains(target)) {
        onExpandedChange(false);
      }
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
    };
  }, [expanded, onExpandedChange]);

  useEffect(() => {
    if (!showId || !pendingInteractionId) {
      return;
    }

    let cancelled = false;
    const poll = (): void => {
      void api
        .getShowInteraction(showId, pendingInteractionId)
        .then(async (result) => {
          if (cancelled) {
            return;
          }
          if (result.interaction.status === 'WAITING') {
            return;
          }
          setPendingInteractionId(null);
          if (result.interaction.status === 'ACCEPTED') {
            setStatusNote(t('common.show.tipMenuAccepted'));
          } else if (result.interaction.status === 'DECLINED') {
            setStatusNote(t('common.show.tipMenuDeclined'));
            const wallet = await api.getSpendWallet();
            onBalanceChange(wallet.total);
          } else {
            setStatusNote(null);
          }
        })
        .catch(() => undefined);
    };

    poll();
    const intervalId = window.setInterval(poll, 2_500);
    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [api, onBalanceChange, pendingInteractionId, showId]);

  const quickItems = items.filter((item) => item.isQuickButton);
  const hasMenu = items.length > 0;

  if (!loaded || !hasMenu) {
    return null;
  }

  const queueMenuItem = (item: TipMenuItemSummary): void => {
    requireAuth(() => {
      if (!showId || !canTip || isTipping || busy) {
        return;
      }
      if (!requireCoins(coinBalance, item.amountCoins, () => {
        queueMenuItem(item);
      })) {
        return;
      }
      setBusy(true);

      const request = item.requiresAction
        ? api.createShowInteraction(showId, {
            kind: 'TIP_MENU',
            label: item.label,
            amountCoins: item.amountCoins,
            tipMenuItemId: item.id,
          })
        : api.sendTip({
            showId,
            amountCoins: item.amountCoins,
            message: item.label,
          });

      void request
        .then(async (result) => {
          const wallet = await api.getSpendWallet();
          onBalanceChange(wallet.total);
          if (item.requiresAction && 'interaction' in result) {
            setPendingInteractionId(result.interaction.id);
            setStatusNote(t('common.show.tipMenuQueued'));
          } else {
            setPendingInteractionId(null);
            setStatusNote(t('common.show.tipMenuTipped'));
          }
          onMenuOpenChange(false);
          onExpandedChange(false);
        })
        .catch(() => undefined)
        .finally(() => {
          setBusy(false);
        });
    }, 'tip');
  };

  const handleTipMenuClick = (): void => {
    requireAuth(() => {
      if (quickItems.length > 0) {
        onExpandedChange(!expanded);
        return;
      }
      onMenuOpenChange(true);
    }, 'tip');
  };

  return (
    <div className="show-room__tip-group" ref={groupRef}>
      <Button
        label={t('common.show.tipMenuButton')}
        variant="tipMenu"
        onClick={handleTipMenuClick}
      />

      {statusNote ? (
        <p className="show-room__tip-status" role="status">
          {statusNote}
        </p>
      ) : null}

      <div
        className={`show-room__tip-slide${expanded ? ' show-room__tip-slide--open' : ''}`}
        aria-hidden={!expanded}
      >
        <div className="show-room__tip-slide-inner" aria-label={t('common.show.tipMenuTitle')}>
          {quickItems.map((item) => (
            <Button
              key={item.id}
              label={`${item.label} · ${String(item.amountCoins)}`}
              variant="chip"
              disabled={busy || isTipping}
              onClick={() => {
                queueMenuItem(item);
              }}
            />
          ))}
          {hasMenu ? (
            <Button
              label={t('common.show.tipMenuTitle')}
              variant="chip"
              onClick={() => {
                requireAuth(() => {
                  onExpandedChange(false);
                  onMenuOpenChange(true);
                }, 'tip');
              }}
            />
          ) : null}
        </div>
      </div>

      {menuOpen && playerEl
        ? createPortal(
            <PlayerMenuDock
              titleId="tip-menu-dock-title"
              title={t('common.show.tipMenuTitle')}
              hint={t('common.show.tipMenuQueueHint')}
              emptyLabel={t('common.show.tipMenuEmpty')}
              closeLabel={t('common.show.tipMenuClose')}
              items={items.map((item) => ({
                id: item.id,
                label: item.label,
                amountCoins: item.amountCoins,
                disabled: busy || isTipping || !canTip,
              }))}
              onSelect={(dockItem) => {
                const item = items.find((row) => row.id === dockItem.id);
                if (item) {
                  queueMenuItem(item);
                }
              }}
              onClose={() => {
                onMenuOpenChange(false);
              }}
            />,
            playerEl,
          )
        : null}
    </div>
  );
}
